/**
 * `PluginToolbar` — read-only / measurement / annotation / profile /
 * viewshed / export plugin row.
 *
 * WP-11 design-system compliance sweep:
 *   - Removed the inline `<span class="material-symbols-outlined">`
 *     glyphs in favour of the design-bridge `<Icon name="…" />`
 *     primitive (rounded variant, Material Symbols Rounded font is
 *     loaded once via the design bridge). One icon variant
 *     platform-wide per `ICON_SYSTEM_PLAYBOOK.md`.
 *   - Switched the spinner glyph from `hourglass_empty` (not in the
 *     registry) to `pending` (registry-approved status / lifecycle
 *     icon).
 *   - Removed all dependence on `styles/global.css` for chrome — the
 *     row, button, label, and shortcut hints now resolve to
 *     `tokens.*` (color, density, radius, font, motion).
 *   - 40 px touch target and visible focus ring per
 *     `DESIGN_POLICY.md` §5; pressed / disabled / error states
 *     all carry both color and a non-color cue (icon swap or
 *     suffix label).
 */

import { Icon, Stack, Tooltip, tokens, type IconName } from '@/design';
import { usePluginStore, type PluginId } from '@/state/usePluginStore';
import { useViewerStore } from '@/state/useViewerStore';

interface PluginToolbarEntry {
  id: PluginId;
  label: string;
  icon: IconName;
  shortcut?: string;
}

const PLUGIN_ENTRIES: PluginToolbarEntry[] = [
  { id: 'measurement', label: 'Measure', icon: 'straighten', shortcut: 'M' },
  { id: 'annotation', label: 'Annotate', icon: 'edit_note', shortcut: 'A' },
  { id: 'profile', label: 'Profile', icon: 'query_stats', shortcut: 'P' },
  { id: 'viewshed', label: 'Viewshed', icon: 'visibility', shortcut: 'V' },
  { id: 'export', label: 'Export', icon: 'ios_share', shortcut: 'E' },
  { id: 'terrain-analysis', label: 'Terrain', icon: 'terrain', shortcut: 'T' },
];

export function PluginToolbar() {
  const {
    activePluginId,
    setActivePlugin,
    isPluginAvailable,
    getPluginState,
    engineType,
    engineReady,
  } = usePluginStore();
  const { readOnly } = useViewerStore();

  const handlePluginClick = (pluginId: PluginId) => {
    if (readOnly && pluginId !== 'export') return;
    if (!isPluginAvailable(pluginId)) return;
    setActivePlugin(activePluginId === pluginId ? null : pluginId);
  };

  const getButtonState = (pluginId: PluginId) => {
    const available = isPluginAvailable(pluginId);
    const state = getPluginState(pluginId);
    const isActive = activePluginId === pluginId;

    if (readOnly && pluginId !== 'export') {
      return { disabled: true, tooltip: `${pluginId} requires editor role` };
    }

    if (!available) {
      if (!engineType) {
        return { disabled: true, tooltip: `${pluginId} requires map engine` };
      }
      if (!engineReady) {
        return {
          disabled: true,
          tooltip: `${pluginId} waiting for ${engineType} engine`,
        };
      }
      return {
        disabled: true,
        tooltip: `${pluginId} not available for ${engineType} engine`,
      };
    }

    if (state === 'backend-not-ready') {
      return { disabled: true, tooltip: `${pluginId} backend not ready` };
    }

    if (state === 'error') {
      return {
        disabled: false,
        tooltip: `${pluginId} error - click to retry`,
        hasError: true,
      };
    }

    const toolbarEntry = PLUGIN_ENTRIES.find((e) => e.id === pluginId);
    return {
      disabled: false,
      tooltip: `${pluginId}${toolbarEntry?.shortcut ? ` (${toolbarEntry.shortcut})` : ''}`,
      isActive,
    };
  };

  return (
    <Stack
      direction="row"
      gap={1}
      role="toolbar"
      aria-label="Plugin tools"
      style={{
        flexWrap: 'wrap',
        padding: tokens.space[1],
      }}
    >
      {PLUGIN_ENTRIES.map((entry) => {
        const buttonState = getButtonState(entry.id);
        const pluginState = getPluginState(entry.id);
        const isActive = Boolean(buttonState.isActive);
        const hasError = Boolean(buttonState.hasError);
        const isComputing = pluginState === 'computing';

        const button = (
          <button
            type="button"
            disabled={buttonState.disabled}
            onClick={() => handlePluginClick(entry.id)}
            aria-label={buttonState.tooltip}
            aria-pressed={isActive}
            aria-disabled={buttonState.disabled}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: tokens.space[2],
              minHeight: tokens.density.touchMin,
              paddingInline: tokens.space[3],
              border: tokens.border.thin,
              borderRadius: tokens.radius.input,
              borderColor: hasError
                ? tokens.color.semantic.danger
                : isActive
                  ? tokens.color.accent.primary
                  : 'var(--md-outline-variant)',
              background: isActive
                ? tokens.color.accent.subtle
                : tokens.color.surface[1],
              color: buttonState.disabled
                ? tokens.color.ink.muted
                : tokens.color.ink.primary,
              fontFamily: tokens.font.family.ui,
              fontSize: tokens.font.size.sm,
              cursor: buttonState.disabled ? 'not-allowed' : 'pointer',
            }}
          >
            <Icon name={entry.icon} size={20} decorative />
            {isComputing ? (
              // Non-color cue for the "running" state — the icon swap
              // explains the state independent of the color change.
              <Icon name="pending" size={16} decorative />
            ) : null}
            <span>{entry.label}</span>
            {entry.shortcut ? (
              <span
                aria-hidden="true"
                style={{
                  fontFamily: tokens.font.family.mono,
                  fontSize: tokens.font.size.xs,
                  color: tokens.color.ink.secondary,
                }}
              >
                {entry.shortcut}
              </span>
            ) : null}
          </button>
        );

        return (
          <Tooltip key={entry.id} content={buttonState.tooltip}>
            {button}
          </Tooltip>
        );
      })}
    </Stack>
  );
}

